import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../apiClient';
import { uploadFile } from '../api';
import { guestApiPath } from '../guestApiPath';
import {
  GUEST_AUTHENTICATED_EVENT,
  getGuestAuthFailureMessage,
  getGuestAuthFailureReason,
  getLocalGuestTokenFailureReason,
  invalidateGuestSession,
  isActiveGuestGallery,
} from '../utils/guestAuthRedirect';
import {
  checkBuster,
  clearAllFiles,
  isOPFSAvailable,
  loadBlob,
  loadQueue,
  readFile,
  removeBlob,
  removeFile,
  saveQueue,
  storeBlob,
  sweepUnreferencedFiles,
  writeFile,
} from '../utils/uploadQueueStorage';
import { getUploadErrorDetails } from '../utils/uploadErrors';
import { getGuestAuthToken } from '../utils/guestToken';
import { getUploadFileType } from '../utils/mediaFileType';
import { decodeJwtPayload } from '../utils/decodeJwtPayload';
import {
  currentGuestUploadNotice, ensureGuestUploadAcknowledgement,
  GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, readGuestUploadAcknowledgement,
  resetGuestUploadNoticeDelivery, GUEST_UPLOAD_NOTICE_CHANGED,
} from '../utils/guestUploadNotice';

const UploadQueueContext = createContext(null);
const BASE_RETRY_DELAY_MS = 30_000;
const MAX_RETRY_DELAY_MS = 30 * 60_000;
const MAX_RETRY_ATTEMPTS = 5;
const RESET_RETRY_META = {
  retryCount: 0,
  retryAfter: null,
  retryExhausted: false,
  authPaused: false,
  noticePaused: false,
  policyBlocked: false,
};

const now = () => Date.now();

const createId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const getErrorMessage = (error) => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Upload failed';
};

const chainMutation = (chainRef, fn) => {
  const promise = chainRef.current
    .catch(() => {})
    .then(fn);
  chainRef.current = promise.catch(() => {});
  return promise;
};

// Three-way shallow rollback for immutable, flat queue records. Queue mutations
// must replace changed items rather than mutating them in place. If the queue
// schema gains nested mutable values, this merge contract must be extended.
const rollbackFailedQueueCommit = (currentQueue, previousQueue, failedQueue) => {
  const previousById = new Map(previousQueue.map((item) => [item.id, item]));
  const failedById = new Map(failedQueue.map((item) => [item.id, item]));

  const rolledBackQueue = currentQueue.flatMap((currentItem) => {
    const failedItem = failedById.get(currentItem.id);
    if (!failedItem) {
      return [currentItem];
    }

    const previousItem = previousById.get(currentItem.id);
    if (!previousItem) {
      // The failed commit added this row, so it must not survive solely because
      // a concurrent commit copied or updated it before persistence rejected.
      return [];
    }

    const restoredItem = { ...currentItem };
    const keys = new Set([
      ...Object.keys(previousItem),
      ...Object.keys(failedItem),
    ]);
    keys.forEach((key) => {
      if (previousItem[key] === failedItem[key]) {
        return;
      }

      // Revert only fields that still contain the failed commit's value.
      // A concurrent mutation that changed the same field wins.
      if (currentItem[key] === failedItem[key]) {
        if (Object.prototype.hasOwnProperty.call(previousItem, key)) {
          restoredItem[key] = previousItem[key];
        } else {
          delete restoredItem[key];
        }
      }
    });
    return [restoredItem];
  });

  previousQueue.forEach((previousItem, previousIndex) => {
    if (failedById.has(previousItem.id)) {
      return;
    }
    if (rolledBackQueue.some((item) => item.id === previousItem.id)) {
      return;
    }

    // The failed commit removed this row. Restore it near its original position
    // while preserving rows added by a concurrent mutation.
    rolledBackQueue.splice(
      Math.min(previousIndex, rolledBackQueue.length),
      0,
      previousItem
    );
  });

  return rolledBackQueue;
};

const removeItemStorage = async (itemId) => {
  try {
    await removeBlob(itemId);
  } catch {
    // OK if blob doesn't exist
  }
  try {
    await removeFile(itemId);
  } catch {
    // Removing a queue item should not fail because its file is already gone.
  }
};

const recoverStartupQueue = (items) => {
  const timestamp = now();
  let changed = false;
  const queue = items.map((item) => {
    if (item.status !== 'uploading') {
      return item;
    }

    changed = true;
    return {
      ...item,
      status: 'pending',
      progress: 0,
      error: null,
      noticePaused: false,
      updatedAt: timestamp,
    };
  });

  return { queue, changed };
};

const isMounted = (mountedRef) => mountedRef.current;
const isExplicitlyOnline = () => navigator.onLine === true;
const isDocumentVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';
const getQueueTokenFailureReason = (token, galleryId) => {
  const reason = getLocalGuestTokenFailureReason(token, galleryId);
  const tokenGalleryId = decodeJwtPayload(token)?.galleryId;
  // The shared check reports expiration before checking the gallery claim. An
  // unrelated expired session must never be recovered on this queue row's behalf.
  if (reason === 'expired' && (typeof tokenGalleryId !== 'string' || tokenGalleryId.trim() !== String(galleryId).trim())) {
    return 'denied';
  }
  return reason;
};
const getRetryDelay = (retryCount) =>
  Math.round(
    Math.min(
      BASE_RETRY_DELAY_MS * 2 ** Math.max(retryCount - 1, 0),
      MAX_RETRY_DELAY_MS
    ) *
      (0.8 + Math.random() * 0.4)
  );

export function UploadQueueProvider({ children }) {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const queueRef = useRef([]);
  const isReadyRef = useRef(false);
  const mountedRef = useRef(true);
  const persistenceChainRef = useRef(Promise.resolve());
  // User-triggered queue mutations share one chain. Drain commits stay
  // non-blocking and rely on structural rollback if persistence fails.
  const mutationChainRef = useRef(Promise.resolve());
  const initPromiseRef = useRef(null);
  const progressThrottleRef = useRef(0);
  const drainRunningRef = useRef(false);
  const drainAgainRef = useRef(false);
  const drainPromiseRef = useRef(Promise.resolve());
  const runDrainRef = useRef(null);
  const activeUploadsRef = useRef(new Map());

  const applyQueue = useCallback((nextQueue) => {
    queueRef.current = nextQueue;

    if (mountedRef.current) {
      setQueue(nextQueue);
    }
  }, []);

  const persistQueue = useCallback((nextQueue) => {
    persistenceChainRef.current = persistenceChainRef.current
      .catch(() => {
        // Keep later queue saves moving even if an earlier persistence attempt fails.
      })
      .then(() => saveQueue(nextQueue));

    return persistenceChainRef.current;
  }, []);

  const commitQueue = useCallback(async (nextQueue, { persist = true } = {}) => {
    const shouldPersist = persist && isReadyRef.current;
    const previousQueue = shouldPersist ? queueRef.current : null;
    applyQueue(nextQueue);

    if (shouldPersist) {
      try {
        await persistQueue(nextQueue);
      } catch (error) {
        if (queueRef.current === nextQueue) {
          applyQueue(previousQueue);
        } else {
          const rolledBackQueue = rollbackFailedQueueCommit(
            queueRef.current,
            previousQueue,
            nextQueue
          );
          applyQueue(rolledBackQueue);

          // A concurrent commit may already have queued a stale snapshot that
          // copied the failed mutation. Persist the repaired state after it.
          try {
            await persistQueue(rolledBackQueue);
          } catch {
            // Preserve the original commit error. A later mutation or startup
            // recovery will retry persistence from the repaired in-memory state.
          }
        }
        throw error;
      }
    }
  }, [applyQueue, persistQueue]);

  const startInitialization = useCallback(() => {
    if (initPromiseRef.current) {
      return initPromiseRef.current;
    }

    initPromiseRef.current = (async () => {
      try {
        const storedQueue = await loadQueue();
        const erased = await checkBuster(process.env.REACT_APP_VERSION || 'dev');

        if (erased && storedQueue.length > 0) {
          const oldItemIds = storedQueue.map((item) => item.id);
          await clearAllFiles(oldItemIds);
        }

        const workingQueue = erased ? [] : storedQueue;
        const { queue: recoveredQueue, changed } = recoverStartupQueue(workingQueue);

        if (!mountedRef.current) {
          return;
        }

        applyQueue(recoveredQueue);

        const activeItemIds = recoveredQueue
          .filter((item) => item.status !== 'done')
          .map((item) => item.id);
        await sweepUnreferencedFiles(activeItemIds);

        // Done rows are metadata-only after startup; their OPFS directories
        // were already removed by sweepUnreferencedFiles. The storage pass
        // below is for IDB blob cleanup (removeBlob) — removeFile is a no-op here.
        const completedItems = recoveredQueue.filter((item) => item.status === 'done');
        await Promise.all(completedItems.map((item) => removeItemStorage(item.id)));

        if (!mountedRef.current) {
          return;
        }

        isReadyRef.current = true;
        setIsReady(true);

        if (changed) {
          await persistQueue(recoveredQueue);
        }
      } catch (error) {
        console.warn('Failed to initialize upload queue:', error);

        if (!mountedRef.current) {
          return;
        }

        const currentQueue = queueRef.current;
        applyQueue(currentQueue);
        isReadyRef.current = true;
        setIsReady(true);
      }
    })();

    return initPromiseRef.current;
  }, [applyQueue, persistQueue]);

  const ensureReady = useCallback(async () => {
    if (isReadyRef.current) {
      return mountedRef.current;
    }

    if (!mountedRef.current) {
      return false;
    }

    await startInitialization();

    return mountedRef.current && isReadyRef.current;
  }, [startInitialization]);

  const updateItem = useCallback(
    async (itemId, updater) => {
      if (!queueRef.current.some((item) => item.id === itemId)) {
        return undefined;
      }

      const nextQueue = queueRef.current.map((item) =>
        item.id === itemId ? updater(item) : item
      );
      await commitQueue(nextQueue);
      return nextQueue.find((item) => item.id === itemId);
    },
    [commitQueue]
  );

  useEffect(() => {
    const activeUploads = activeUploadsRef.current;

    // React strict mode remounts providers in development; reset the guard on remount.
    mountedRef.current = true;

    // Strict Mode re-mount in dev: if the ref was already set but
    // setIsReady may have been skipped between cleanup and remount,
    // sync the state variable.
    if (isReadyRef.current) {
      setIsReady(true);
    }

    startInitialization();

    return () => {
      mountedRef.current = false;
      activeUploads.forEach(({ controller }) => controller.abort());
      activeUploads.clear();
    };
  }, [startInitialization]);

  const runDrain = useCallback(async () => {
    const ready = await ensureReady();
    if (!ready || !isMounted(mountedRef)) {
      return;
    }

    const succeededGalleryIds = new Set();
    // Per-invocation guard against tight retry loops. Re-entries via
    // drainAgainRef get a fresh set — retryAfter with jitter is the
    // gate across re-entries.
    const attemptedItemIds = new Set();
    const pauseExpiredItem = async (item, token) => {
      // Apply the paused state before announcing auth recovery. Reauthentication can
      // then wake this row even while its metadata is still being persisted.
      const pause = updateItem(item.id, (queueItem) => ({
        ...queueItem,
        status: 'paused',
        error: getGuestAuthFailureMessage('expired'),
        retryAfter: null,
        retryExhausted: false,
        authPaused: true,
        noticePaused: false,
        updatedAt: now(),
      }));
      if (getGuestAuthToken() === token && isActiveGuestGallery(item.galleryId, token)) {
        invalidateGuestSession({ galleryId: item.galleryId, reason: 'expired' });
      }
      try {
        await pause;
      } catch (error) {
        // A failed pause rolls back to uploading when expiry happened during a
        // file read. No request remains active, so allow the next login to drain
        // it again. Startup recovery also resets the persisted uploading row.
        const nextQueue = queueRef.current.map(queueItem =>
          queueItem.id === item.id && queueItem.status === 'uploading'
            ? { ...queueItem, status: 'pending', progress: 0 }
            : queueItem
        );
        applyQueue(nextQueue);
        console.warn('Failed to persist expired upload pause:', error);
      }
    };

    try {
      while (true) {
        if (!isMounted(mountedRef)) {
          return;
        }

        const currentTimestamp = now();
        const currentToken = getGuestAuthToken();
        const currentItem = queueRef.current.find((item) => {
          if (attemptedItemIds.has(item.id)) {
            return false;
          }
          if (!isActiveGuestGallery(item.galleryId, currentToken)) {
            return false;
          }
          const localAuthReason = getQueueTokenFailureReason(currentToken, item.galleryId);
          if (localAuthReason && localAuthReason !== 'expired') {
            return false;
          }
          if (item.status === 'pending') {
            return true;
          }
          return (
            item.status === 'failed' &&
            !item.retryExhausted &&
            (!item.retryAfter || item.retryAfter <= currentTimestamp)
          );
        });
        if (!currentItem) {
          break;
        }
        if (getQueueTokenFailureReason(currentToken, currentItem.galleryId) === 'expired') {
          await pauseExpiredItem(currentItem, currentToken);
          break;
        }

        let uploadingItem;
        const activeUpload = {
          cancelled: false,
          controller: new AbortController(),
        };
        // Register cancellation before exposing the uploading state. This keeps
        // the Cancel action reliable while the blob and signed URL are prepared.
        activeUploadsRef.current.set(currentItem.id, activeUpload);
        let uploadToken = '';
        try {
          uploadingItem = await updateItem(currentItem.id, (item) => ({
            ...item,
            status: 'uploading',
            progress: item.progress || 0,
            error: null,
            noticePaused: false,
            retryAfter: null,
            updatedAt: now(),
          }));
        } catch {
          if (activeUploadsRef.current.get(currentItem.id) === activeUpload) {
            activeUploadsRef.current.delete(currentItem.id);
          }
          attemptedItemIds.add(currentItem.id);
          continue;
        }
        attemptedItemIds.add(currentItem.id);
        if (!isMounted(mountedRef)) {
          return;
        }
        if (!uploadingItem) {
          if (activeUploadsRef.current.get(currentItem.id) === activeUpload) {
            activeUploadsRef.current.delete(currentItem.id);
          }
          continue;
        }

        progressThrottleRef.current = Date.now();

        try {
          let storedBlob;
          try {
            storedBlob = await loadBlob(uploadingItem.id);
          } catch (error) {
            console.warn('Failed to load upload blob fallback:', error);
          }
          // Check IndexedDB blob first as an in-process cache; fall back to OPFS for durable reads.
          const file = storedBlob ?? (await readFile(uploadingItem.id, uploadingItem.fileName));
          if (!isMounted(mountedRef)) {
            return;
          }
          if (activeUpload.cancelled) {
            continue;
          }

          uploadToken = getGuestAuthToken();
          if (!isActiveGuestGallery(uploadingItem.galleryId, uploadToken)) {
            await updateItem(uploadingItem.id, () => ({
              ...currentItem,
              updatedAt: now(),
            }));
            continue;
          }
          const localAuthReason = getQueueTokenFailureReason(uploadToken, uploadingItem.galleryId);
          if (localAuthReason) {
            if (localAuthReason === 'expired') {
              await pauseExpiredItem(uploadingItem, uploadToken);
              break;
            } else {
              await updateItem(uploadingItem.id, () => ({ ...currentItem, updatedAt: now() }));
            }
            continue;
          }

          await ensureGuestUploadAcknowledgement(uploadingItem.galleryId, uploadToken,
            uploadingItem.guestUploadNotice, activeUpload.controller.signal);
          if (getGuestAuthToken() !== uploadToken) {
            await updateItem(uploadingItem.id, (item) => ({ ...item, status: 'pending', updatedAt: now() }));
            drainAgainRef.current = true;
            continue;
          }

          const response = await apiClient.post(
            guestApiPath('/upload-url', uploadingItem.galleryId),
            {
              fileName: uploadingItem.fileName,
              contentType: uploadingItem.contentType,
              uploadId: uploadingItem.id,
            },
            {
              headers: {
                Authorization: `Bearer ${uploadToken}`,
              },
              signal: activeUpload.controller.signal,
            }
          );
          if (!isMounted(mountedRef)) {
            return;
          }
          if (activeUpload.cancelled) {
            continue;
          }

          const signedUrl = response?.data?.url || response?.data?.uploadUrl;

          if (!signedUrl) {
            throw new Error('Upload URL missing');
          }

          await uploadFile(signedUrl, file, (progress) => {
            if (!isMounted(mountedRef)) {
              return;
            }

            let changed = false;
            const nextQueue = queueRef.current.map((item) => {
              if (item.id !== uploadingItem.id || item.status !== 'uploading') {
                return item;
              }

              changed = true;
              return { ...item, progress, updatedAt: now() };
            });

            if (!changed) {
              return;
            }

            applyQueue(nextQueue);

            // Progress is best-effort and intentionally skips commitQueue
            // rollback — the per-item path below persists the final status
            // through commitQueue, which is the authoritative write.
            const elapsed = Date.now() - progressThrottleRef.current;
            if (isReadyRef.current && elapsed >= 300) {
              progressThrottleRef.current = Date.now();
              persistQueue(nextQueue).catch(() => {});
            }
          }, undefined, activeUpload.controller.signal, response?.data?.requiredHeaders);
          if (!isMounted(mountedRef)) {
            return;
          }
          if (activeUpload.cancelled) {
            continue;
          }

          await updateItem(uploadingItem.id, (item) => ({
            ...item,
            status: 'done',
            originalPath: response?.data?.originalPath || response?.data?.fileName || null,
            mediaId: response?.data?.mediaId || null,
            progress: 100,
            error: null,
            ...RESET_RETRY_META,
            updatedAt: now(),
          }));
          if (!isMounted(mountedRef)) {
            return;
          }

          // Refresh each completed file immediately; a long queue may continue for minutes.
          succeededGalleryIds.add(uploadingItem.galleryId);
          try {
            Promise.resolve(queryClient.invalidateQueries({
              predicate: query => query.queryKey[0] === 'media' &&
                query.queryKey[1] === 'infinite' &&
                query.queryKey[query.queryKey.length - 1] === uploadingItem.galleryId,
            })).catch(() => {});
          } catch {
            // Cache refresh failures must not mark an uploaded file as failed.
          }
          await removeItemStorage(uploadingItem.id);
        } catch (error) {
          if (!isMounted(mountedRef)) {
            return;
          }

          if (activeUpload?.cancelled) {
            continue;
          }

          const timestamp = now();
          const authReason =
            error?.guestAuthFailure?.reason || getGuestAuthFailureReason(error);
          if (error?.response?.data?.error === 'UPLOAD_NOTICE_REQUIRED') {
            await updateItem(uploadingItem.id, (item) => ({
              ...item, status: 'paused', noticePaused: true, authPaused: false,
              error: 'Read and confirm the current upload notice before retrying these files.',
              retryAfter: null, retryExhausted: false, updatedAt: timestamp,
            }));
            await resetGuestUploadNoticeDelivery(uploadingItem.galleryId, uploadToken, error.response.data.version);
          } else if (authReason) {
            const currentToken = getGuestAuthToken();
            const hasReplacementToken =
              currentToken &&
              currentToken !== uploadToken &&
              !getLocalGuestTokenFailureReason(
                currentToken,
                uploadingItem.galleryId
              );
            await updateItem(uploadingItem.id, (item) => ({
              ...item,
              status: hasReplacementToken ? 'pending' : 'paused',
              error: hasReplacementToken
                ? null
                : getGuestAuthFailureMessage(authReason),
              retryAfter: null,
              retryExhausted: false,
              authPaused: !hasReplacementToken,
              updatedAt: timestamp,
            }));
            if (hasReplacementToken) {
              drainAgainRef.current = true;
            }
          } else {
            const uploadError = getUploadErrorDetails(error);
            if (uploadError.policyBlocked) {
              await updateItem(uploadingItem.id, (item) => ({
                ...item,
                status: 'failed',
                error: uploadError.message,
                retryAfter: null,
                retryExhausted: true,
                authPaused: false,
                policyBlocked: true,
                updatedAt: timestamp,
              }));
            } else {
              const retryCount = (uploadingItem.retryCount || 0) + 1;
              const retryExhausted = retryCount >= MAX_RETRY_ATTEMPTS;
              await updateItem(uploadingItem.id, (item) => ({
                ...item,
                status: 'failed',
                error: uploadError.message || getErrorMessage(error),
                retryCount,
                retryAfter: retryExhausted ? null : timestamp + getRetryDelay(retryCount),
                retryExhausted,
                authPaused: false,
                policyBlocked: false,
                updatedAt: timestamp,
              }));
            }
          }
        } finally {
          if (activeUploadsRef.current.get(uploadingItem.id) === activeUpload) {
            activeUploadsRef.current.delete(uploadingItem.id);
          }
        }
      }
    } finally {
      succeededGalleryIds.forEach((galleryId) => {
        try {
          queryClient.invalidateQueries({
            predicate: (query) => {
              const queryKey = query.queryKey;
              return (
                queryKey[0] === 'media' &&
                queryKey[1] === 'infinite' &&
                queryKey[queryKey.length - 1] === galleryId
              );
            },
          });
        } catch {
          // Upload completion should not leave the processor stuck if cache invalidation fails.
        }
      });
    }
  }, [applyQueue, ensureReady, persistQueue, queryClient, updateItem]);

  useLayoutEffect(() => {
    runDrainRef.current = runDrain;
  }, [runDrain]);

  const processQueue = useCallback(() => {
    if (drainRunningRef.current) {
      drainAgainRef.current = true;
      return drainPromiseRef.current;
    }

    drainRunningRef.current = true;
    if (mountedRef.current) {
      setIsProcessing(true);
    }
    drainPromiseRef.current = (async () => {
      try {
        do {
          drainAgainRef.current = false;
          await runDrainRef.current?.();
        } while (drainAgainRef.current && isMounted(mountedRef));
      } finally {
        drainRunningRef.current = false;
        if (mountedRef.current) {
          setIsProcessing(false);
        }
      }
    })();

    return drainPromiseRef.current;
  }, []);

  const handleDrainError = useCallback((error) => {
    // Defense-in-depth; per-item upload failures are handled inside processQueue.
    console.warn('Failed to process upload queue:', error);
  }, []);

  const requestDrain = useCallback(() => {
    return processQueue().catch(handleDrainError);
  }, [handleDrainError, processQueue]);

  const wakeGalleryQueue = useCallback(
    async (galleryId) => {
      const ready = await ensureReady();
      if (!ready || !isMounted(mountedRef)) {
        return;
      }
      const notice = currentGuestUploadNotice(galleryId);
      const canResumeNotice = !notice.required || notice.acknowledged;
      const acknowledgement = notice.required && notice.acknowledged
        ? readGuestUploadAcknowledgement(galleryId, notice.active.guestNoticeVersion) : null;
      const nextQueue = queueRef.current.map((item) =>
        item.galleryId === galleryId && item.status === 'paused'
          && (item.authPaused || (item.noticePaused && canResumeNotice))
          ? {
              ...item,
              status: 'pending',
              error: null,
              retryAfter: null,
              retryExhausted: false,
              authPaused: false,
              noticePaused: false,
              ...(acknowledgement ? { guestUploadNotice: acknowledgement } : {}),
              updatedAt: now(),
            }
          : item
      );
      if (nextQueue.some((item, index) => item !== queueRef.current[index])) {
        await commitQueue(nextQueue);
      }
      if (isExplicitlyOnline() && isDocumentVisible()) {
        await requestDrain();
      }
    },
    [commitQueue, ensureReady, requestDrain]
  );

  useEffect(() => {
    const handleGuestAuthenticated = (event) => {
      const galleryId =
        typeof event.detail?.galleryId === 'string' ? event.detail.galleryId.trim() : '';
      if (galleryId) {
        wakeGalleryQueue(galleryId).catch(handleDrainError);
      }
    };
    window.addEventListener(GUEST_AUTHENTICATED_EVENT, handleGuestAuthenticated);
    window.addEventListener(GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, handleGuestAuthenticated);
    window.addEventListener(GUEST_UPLOAD_NOTICE_CHANGED, handleGuestAuthenticated);
    return () => {
      window.removeEventListener(GUEST_AUTHENTICATED_EVENT, handleGuestAuthenticated);
      window.removeEventListener(GUEST_UPLOAD_NOTICE_ACKNOWLEDGED, handleGuestAuthenticated);
      window.removeEventListener(GUEST_UPLOAD_NOTICE_CHANGED, handleGuestAuthenticated);
    };
  }, [handleDrainError, wakeGalleryQueue]);

  const enqueue = useCallback(
    (files, galleryId) => {
      const runEnqueue = async () => {
        const ready = await ensureReady();
        if (!ready || !isMounted(mountedRef)) {
          return [];
        }

        const fileList = Array.from(files || []);
        if (!fileList.length) {
          return [];
        }

        const notice = currentGuestUploadNotice(galleryId);
        if (notice.required && !notice.acknowledged) {
          throw new Error('Read and confirm the upload notice before adding files.');
        }
        const guestUploadNotice = notice.required
          ? readGuestUploadAcknowledgement(galleryId, notice.active.guestNoticeVersion) : null;

        const opfsAvailable = isOPFSAvailable();
        if (!opfsAvailable) {
          console.warn('OPFS unavailable; storing upload queue files in IndexedDB.');
        }

        const createdItems = [];
        const cleanupCreatedItems = async () => {
          await Promise.all(createdItems.map((item) => removeItemStorage(item.id)));
        };

        try {
          for (const file of fileList) {
            if (!isMounted(mountedRef)) {
              await cleanupCreatedItems();
              return [];
            }

            const timestamp = now();
            const item = {
              id: createId(),
              galleryId,
              fileName: file.name,
              // A blank browser type (MOV/HEIC/HEIF on some platforms) is replaced by the type
              // inferred from the extension so the API accepts the item on every retry.
              contentType:
                getUploadFileType(file).mimeType || file.type || 'application/octet-stream',
              fileSize: file.size,
              ...(guestUploadNotice ? { guestUploadNotice } : {}),
              status: 'pending',
              progress: 0,
              error: null,
              ...RESET_RETRY_META,
              createdAt: timestamp,
              updatedAt: timestamp,
            };

            if (opfsAvailable) {
              // If the tab closes before commit, startup sweep removes this unreferenced directory.
              await writeFile(item.id, file.name, file);
              if (!isMounted(mountedRef)) {
                createdItems.push(item);
                await cleanupCreatedItems();
                return [];
              }
            } else {
              if (!isMounted(mountedRef)) {
                await cleanupCreatedItems();
                return [];
              }
              await storeBlob(item.id, file);
            }

            createdItems.push(item);
          }

          if (!isMounted(mountedRef)) {
            await cleanupCreatedItems();
            return [];
          }

          const nextQueue = [...queueRef.current, ...createdItems];
          await commitQueue(nextQueue);
        } catch (error) {
          await cleanupCreatedItems();
          throw error;
        }

        if (isExplicitlyOnline()) {
          requestDrain();
        }
        return createdItems;
      };

      // Unlike retryItem, enqueue can reject — write failures and
      // persist failures propagate. Callers must handle the rejection.
      return chainMutation(mutationChainRef, runEnqueue);
    },
    [commitQueue, ensureReady, requestDrain]
  );

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    const drainQueue = () => {
      if (!isExplicitlyOnline() || !isDocumentVisible()) {
        return;
      }
      requestDrain();
    };

    const drainVisibleQueue = () => {
      if (document.visibilityState === 'visible') {
        drainQueue();
      }
    };

    drainQueue();
    window.addEventListener('online', drainQueue);
    document.addEventListener('visibilitychange', drainVisibleQueue);

    return () => {
      window.removeEventListener('online', drainQueue);
      document.removeEventListener('visibilitychange', drainVisibleQueue);
    };
  }, [isReady, requestDrain]);

  useEffect(() => {
    if (!isReady) {
      return undefined;
    }

    const nextRetryAt = queue.reduce((earliestRetryAt, item) => {
      if (
        item.status !== 'failed' ||
        item.retryExhausted ||
        typeof item.retryAfter !== 'number'
      ) {
        return earliestRetryAt;
      }
      return earliestRetryAt === null
        ? item.retryAfter
        : Math.min(earliestRetryAt, item.retryAfter);
    }, null);

    if (nextRetryAt === null) {
      return undefined;
    }

    const retryTimer = window.setTimeout(() => {
      if (isExplicitlyOnline() && isDocumentVisible()) {
        requestDrain();
      }
    }, Math.max(0, nextRetryAt - Date.now()));

    return () => window.clearTimeout(retryTimer);
  }, [isReady, queue, requestDrain]);

  const removeItem = useCallback(
    async (id, { force = false } = {}) => {
      const activeUpload = force ? activeUploadsRef.current.get(id) : null;
      if (activeUpload) {
        activeUpload.cancelled = true;
        activeUpload.controller.abort();
      }

      const runRemoveItem = async () => {
        const ready = await ensureReady();
        if (!ready || !isMounted(mountedRef)) {
          return false;
        }

        const item = queueRef.current.find((queueItem) => queueItem.id === id);
        if (!item) {
          return false;
        }

        if (item.status === 'uploading') {
          if (!force || !activeUpload) {
            return false;
          }

          await updateItem(id, (queueItem) => ({
            ...queueItem,
            status: 'paused',
            progress: 0,
            error: null,
            ...RESET_RETRY_META,
            updatedAt: now(),
          }));
          return true;
        }

        const nextQueue = queueRef.current.filter((queueItem) => queueItem.id !== id);

        await commitQueue(nextQueue);
        await removeItemStorage(id);
        return true;
      };

      return chainMutation(mutationChainRef, runRemoveItem);
    },
    [commitQueue, ensureReady, updateItem]
  );

  const retryItem = useCallback(
    async (id) => {
      const runRetryItem = async () => {
        const ready = await ensureReady();
        if (!ready || !isMounted(mountedRef)) {
          return false;
        }

        const item = queueRef.current.find((queueItem) => queueItem.id === id);
        if (!item || item.policyBlocked || !['failed', 'paused'].includes(item.status)) {
          return false;
        }

        const retriedItem = await updateItem(id, (queueItem) => ({
          ...queueItem,
          status: 'pending',
          progress: 0,
          error: null,
          ...RESET_RETRY_META,
          updatedAt: now(),
        }));

        if (retriedItem && isExplicitlyOnline()) {
          // Fire-and-forget so the caller's promise resolves as soon
          // as the row flips to pending — the Retry button re-enables
          // immediately while the drain runs to completion in background.
          requestDrain();
        }

        return !!retriedItem;
      };

      // chainMutation's catch-on-assign keeps the chain alive even if a
      // future guard path rejects. Today runRetryItem never rejects —
      // every exit returns false and internal API errors are handled.
      return chainMutation(mutationChainRef, runRetryItem);
    },
    [ensureReady, requestDrain, updateItem]
  );

  const clearCompleted = useCallback(async (galleryId) => {
    const runClearCompleted = async () => {
      const ready = await ensureReady();
      if (!ready || !isMounted(mountedRef)) {
        return 0;
      }

      const completedItems = queueRef.current.filter((item) => item.status === 'done' && (!galleryId || item.galleryId === galleryId));
      const completedIds = new Set(completedItems.map(item => item.id));
      const nextQueue = queueRef.current.filter((item) => !completedIds.has(item.id));

      await commitQueue(nextQueue);
      await Promise.all(completedItems.map((item) => removeItemStorage(item.id)));
      return completedItems.length;
    };

    return chainMutation(mutationChainRef, runClearCompleted);
  }, [commitQueue, ensureReady]);

  const removeItemsForGallery = useCallback(
    async (galleryId, { statuses = null } = {}) => {
      const runRemoveItemsForGallery = async () => {
        const ready = await ensureReady();
        if (!ready || !isMounted(mountedRef)) {
          return 0;
        }

        const allowedStatuses = Array.isArray(statuses) ? new Set(statuses) : null;
        const shouldRemove = (item) =>
          item.galleryId === galleryId &&
          item.status !== 'uploading' &&
          (!allowedStatuses || allowedStatuses.has(item.status));
        const removedItems = queueRef.current.filter(shouldRemove);
        const nextQueue = queueRef.current.filter((item) => !shouldRemove(item));

        await commitQueue(nextQueue);
        await Promise.all(removedItems.map((item) => removeItemStorage(item.id)));
        return removedItems.length;
      };

      return chainMutation(mutationChainRef, runRemoveItemsForGallery);
    },
    [commitQueue, ensureReady]
  );

  const pendingCount = useMemo(
    () => queue.filter((item) => ['pending', 'failed', 'paused'].includes(item.status)).length,
    [queue]
  );

  const value = useMemo(
    () => ({
      queue,
      isProcessing,
      isReady,
      pendingCount,
      enqueue,
      // processQueue is the general drain trigger; retryItem is the per-item retry API.
      processQueue,
      retryItem,
      removeItem,
      clearCompleted,
      removeItemsForGallery,
    }),
    [
      queue,
      isProcessing,
      isReady,
      pendingCount,
      enqueue,
      processQueue,
      retryItem,
      removeItem,
      clearCompleted,
      removeItemsForGallery,
    ]
  );

  return (
    <UploadQueueContext.Provider value={value}>{children}</UploadQueueContext.Provider>
  );
}

export function useUploadQueue() {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error('useUploadQueue must be inside UploadQueueProvider');
  }
  return context;
}
